<template>
  <Timeline :value="props.events" layout="vertical" class="w-full">
    <template #marker="slotProps">
      <i
        :class="getIconClass(slotProps.item.type)"
        class="p-2 rounded-full text-white shadow"
        :style="getIconStyle(slotProps.item.type)"
      />
    </template>

    <template #content="slotProps">
      <div class="p-4 bg-white rounded-md shadow-sm border border-gray-200">
        <div class="text-sm text-gray-500">{{ formatDate(slotProps.item.date) }}</div>
        <div class="font-semibold">{{ slotProps.item.title }}</div>
        <div v-if="slotProps.item.description" class="text-sm text-gray-700 mt-1">
          <span v-html="formatDescription(slotProps.item.description)"></span>
        </div>
        <div v-if="slotProps.item.tags?.length" class="mt-2 flex flex-wrap gap-2">
          <Tag v-for="tag in slotProps.item.tags" :key="tag" :value="tag" class="text-xs" />
        </div>
      </div>
    </template>
  </Timeline>
</template>

<script setup lang="ts">
import Timeline from 'primevue/timeline'
import Tag from 'primevue/tag'
import { formatDate } from '@/utils/formatters'
import { useToast } from 'primevue/usetoast'
import { onUnmounted } from 'vue'

const toast = useToast()

export interface TimelineEvent {
  id?: string
  date: Date
  title: string
  description?: string
  tags?: string[]
  type: 'call' | 'email' | 'transaction' | 'letter' | 'comment' | 'task'
  organizationId?: string
}

const props = defineProps<{
  events: TimelineEvent[]
}>()

function getIconClass(type: TimelineEvent['type']) {
  switch (type) {
    case 'call':
      return 'pi pi-phone bg-blue-500'
    case 'email':
      return 'pi pi-envelope bg-green-500'
    case 'transaction':
      return 'pi pi-dollar bg-yellow-500 text-black'
    case 'letter':
      return 'pi pi-file bg-purple-500'
    case 'comment':
      return 'pi pi-comment bg-purple-500'
    case 'task':
      return 'pi pi-check-square bg-orange-500'
    default:
      return 'pi pi-circle bg-gray-400'
  }
}

function getIconStyle(type: TimelineEvent['type']) {
  return {
    fontSize: '1rem',
    backgroundColor: 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    color: 'white',
    ...(type === 'transaction' ? { color: 'black' } : {}),
  }
}

function formatDescription(description: string) {
  // Email regex pattern
  const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
  // Phone regex pattern (matches various formats)
  const phonePattern = /(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x\d+)?/g

  let formattedText = description

  // Replace emails with clickable spans
  formattedText = formattedText.replace(emailPattern, (match) => {
    return `<span class="text-blue-600 cursor-pointer hover:underline" onclick="navigator.clipboard.writeText('${match}'); window.dispatchEvent(new CustomEvent('copy-success', { detail: '${match}' }));">${match}</span>`
  })

  // Replace phone numbers with clickable spans
  formattedText = formattedText.replace(phonePattern, (match) => {
    return `<span class="text-blue-600 cursor-pointer hover:underline" onclick="navigator.clipboard.writeText('${match}'); window.dispatchEvent(new CustomEvent('copy-success', { detail: '${match}' }));">${match}</span>`
  })

  return formattedText
}

// Listen for copy success events
if (typeof window !== 'undefined') {
  const handleCopySuccess = (event: CustomEvent) => {
    toast.add({
      severity: 'success',
      summary: 'Copied',
      detail: `${event.detail} copied to clipboard`,
      life: 3000,
    })
  }

  window.addEventListener('copy-success', handleCopySuccess as EventListener)

  onUnmounted(() => {
    window.removeEventListener('copy-success', handleCopySuccess as EventListener)
  })
}
</script>

<style scoped>
.p-timeline-event-opposite {
  display: none;
}
</style>
